import type { Job } from "@/data/jobs";

export interface JobFacts {
  town: string;
  area?: string;
  postcode?: string;
  pay: string;
  contract: string;
  schedule?: string;
  duration?: string;
  dbs?: string;
  sector: string;
}

const POSTCODE_PATTERN = /^[A-Z]{1,2}\d{1,2}[A-Z]?(?:\s+\d[A-Z]{2})?$/i;

export const getJobFacts = (job: Job): JobFacts => {
  const parts = job.location.split(",").map(part => part.trim()).filter(Boolean);
  const locationPostcode = parts.find(part => POSTCODE_PATTERN.test(part));
  const namedParts = parts.filter(part => !POSTCODE_PATTERN.test(part));

  return {
    town: namedParts[0] || job.location,
    area: namedParts.length > 1 ? namedParts[namedParts.length - 1] : undefined,
    postcode: job.postcodes?.[0] || locationPostcode,
    pay: job.payRate,
    contract: job.contractType,
    schedule: job.schedule,
    duration: job.duration,
    dbs: job.dbsRequired,
    sector: job.category,
  };
};

export const matchesJobLocation = (job: Job, matchers: string[]): boolean => {
  const location = job.location.toLowerCase();
  const postcodes = (job.postcodes ?? []).map(postcode => postcode.toLowerCase());

  return matchers.some(rawMatcher => {
    const matcher = rawMatcher.toLowerCase().trim();
    return location.includes(matcher) || postcodes.some(postcode => postcode === matcher);
  });
};

const REGION_MATCHERS: Record<string, string[]> = {
  london: ["London", "Greenwich", "Ealing", "Catford", "Tulse Hill", "Lewisham", "Croydon", "Bromley", "SE3"],
  birmingham: ["Birmingham", "Harborne", "B17"],
  coventry: ["Coventry", "CV3", "CV6"],
  "west-midlands": ["West Midlands", "Dudley", "Oldbury", "Smethwick", "Brierley Hill", "Wolverhampton"],
  kent: ["Kent", "Maidstone", "Chatham", "Canterbury", "Tonbridge", "Ashford"],
  manchester: ["Manchester"],
  yorkshire: ["Yorkshire", "Leeds", "Sheffield", "York", "Bradford", "Harrogate", "Halifax", "Wakefield", "Huddersfield"],
  doncaster: ["Doncaster", "DN1", "DN2", "DN3", "DN4", "DN5", "DN6", "DN7", "DN8", "DN9", "DN10", "DN11", "DN12"],
  sussex: ["Sussex", "Brighton", "Hove", "Hastings", "St Leonards-on-Sea"],
  bristol: ["Bristol"],
  worcestershire: ["Worcestershire", "Hagley", "Worcester", "Stourport", "Kidderminster", "DY8", "DY10", "DY11", "DY13"],
  dorset: ["Dorset", "Bournemouth", "BH5"],
  oxfordshire: ["Oxfordshire", "Oxford", "Banbury"],
  merseyside: ["Merseyside", "Liverpool"],
  hertfordshire: ["Hertfordshire"],
  cornwall: ["Cornwall", "Camborne", "TR14", "TR15", "TR16"],
  somerset: ["Somerset", "Bridgwater", "Taunton", "Weston-super-Mare"],
  "bridgwater-with-ta6": ["Bridgwater", "TA6", "TA7"],
  "camborne-with-tr14": ["Camborne", "TR14"],
  shropshire: ["Shropshire", "Shifnal", "TF11"],
  hampshire: ["Hampshire", "Portsmouth"],
  staffordshire: ["Staffordshire", "Newcastle-under-Lyme"],
  buckinghamshire: ["Buckinghamshire", "Milton Keynes"],
  "isle-of-wight": ["Isle of Wight", "Newport"],
  "south-wales": ["South Wales", "Caerphilly"],
  surrey: ["Surrey", "Staines", "TW18"],
};

export const getMatchingRegion = <T extends { slug: string }>(job: Job, regions: T[]): T | undefined =>
  regions
    .filter(region => matchesJobLocation(job, REGION_MATCHERS[region.slug] ?? []))
    .sort((a, b) => {
      const specificSlugs = ["bridgwater-with-ta6", "camborne-with-tr14", "doncaster"];
      const aSpecificity = specificSlugs.includes(a.slug) ? 1 : 0;
      const bSpecificity = specificSlugs.includes(b.slug) ? 1 : 0;
      return bSpecificity - aSpecificity;
    })[0];

export const buildJobMetaDescription = (job: Job): string => {
  const facts = getJobFacts(job);
  const segments = [
    `${job.title} job in ${facts.town}`,
    facts.area && facts.area !== facts.town ? facts.area : undefined,
    facts.postcode,
    facts.pay,
    facts.duration,
    facts.schedule,
    facts.dbs,
    "Apply with PAMP Recruitment.",
  ].filter((segment): segment is string => Boolean(segment));

  let description = "";
  for (const segment of segments) {
    const candidate = description ? `${description}. ${segment}` : segment;
    if (candidate.length > 160) continue;
    description = candidate;
  }
  return description.endsWith(".") ? description : `${description}.`;
};

export const buildJobKeywords = (job: Job): string => {
  const facts = getJobFacts(job);
  return [
    `${job.title} ${facts.town}`,
    `${facts.sector} jobs ${facts.town}`,
    facts.area ? `${facts.sector} jobs ${facts.area}` : undefined,
    facts.postcode ? `${facts.postcode} jobs` : undefined,
    facts.dbs ? `${facts.dbs} jobs ${facts.town}` : undefined,
    job.contractType,
    "PAMP Recruitment",
  ].filter(Boolean).join(", ");
};

export const buildCandidateAnswers = (job: Job) => {
  const facts = getJobFacts(job);
  const place = [facts.town, facts.area, facts.postcode].filter(Boolean).join(", ");

  return [
    { question: "What is the role?", answer: `${job.title} in the ${job.category} sector.` },
    { question: "Where is the job?", answer: place || job.location },
    { question: "What is the pay?", answer: job.payRate },
    facts.schedule ? { question: "What are the working hours?", answer: facts.schedule } : undefined,
    facts.duration ? { question: "How long does the role last?", answer: facts.duration } : undefined,
    facts.dbs ? { question: "Is a DBS check required?", answer: facts.dbs } : undefined,
    { question: "How do I apply?", answer: "Apply directly on this page using the online form or email PAMP Recruitment." },
  ].filter((item): item is { question: string; answer: string } => Boolean(item));
};